// Function index:
//   mountShopPanel({ partyId, selfCharId, ws }) — the Trade panel: #shop-btn
//       toggles an overlay with a Wares tab (buy) and a Sell-goods tab
//       (sell the character's own inventory; the offer lands with the
//       response). All prices come from the server payload; the panel never
//       computes one.
//
// Self-contained: unlike the ledger/board/quest overlays (whose markup lives
// in index.html), this panel builds its own DOM and appends it to <body> —
// only the trigger button (#shop-btn) is static markup.

import { buyItem, getInventory, getShop, sellItem, type ShopPayload } from '../api.js';
import type { WS } from '../ws.js';

export function mountShopPanel(opts: { partyId: number; selfCharId: number | null; ws: WS }): void {
  const btn = document.getElementById('shop-btn') as HTMLButtonElement | null;
  if (!btn) return;

  const panel = document.createElement('div');
  panel.id = 'shop-panel';
  panel.hidden = true;
  document.body.appendChild(panel);
  // The visible card; panel itself is the dimmed full-screen backdrop.
  const box = document.createElement('div');
  box.className = 'shop-box';
  panel.appendChild(box);

  let tab: 'wares' | 'sell' = 'wares';
  let open = false;

  const refresh = async (): Promise<void> => {
    if (!open) return;
    const payload = await getShop(opts.partyId);
    box.innerHTML = '';
    box.appendChild(header(payload));
    if (!payload.shop) {
      const p = document.createElement('p');
      p.className = 'party-meta';
      p.textContent = 'No merchant nearby — find a market, forge or wharf.';
      box.appendChild(p);
      return;
    }
    if (tab === 'wares') await renderWares(payload);
    else await renderSell();
  };

  const header = (payload: ShopPayload): HTMLElement => {
    const h = document.createElement('div');
    h.className = 'shop-header';
    const title = payload.shop
      ? `${payload.shop.name}${payload.shop.keeper ? ` — ${payload.shop.keeper}` : ''}`
      : 'Trade';
    h.innerHTML = `<strong>${esc(title)}</strong> <span class="party-meta">${Number(payload.coin)} coin</span>`;
    const tabs = document.createElement('div');
    tabs.className = 'shop-tabs';
    for (const [key, label] of [['wares', 'Wares'], ['sell', 'Sell goods']] as const) {
      const t = document.createElement('button');
      t.type = 'button';
      t.className = 'secondary';
      t.textContent = label;
      t.disabled = tab === key || !payload.shop;
      t.addEventListener('click', () => { tab = key; void refresh(); });
      tabs.appendChild(t);
    }
    h.appendChild(tabs);
    const close = document.createElement('button');
    close.type = 'button';
    close.className = 'secondary shop-close';
    close.textContent = '×';
    close.addEventListener('click', () => { open = false; panel.hidden = true; });
    h.appendChild(close);
    return h;
  };

  const renderWares = async (payload: ShopPayload): Promise<void> => {
    for (const w of payload.shop!.wares) {
      const row = document.createElement('div');
      row.className = 'party-row';
      row.innerHTML = `<div><div>${esc(w.name)}</div><div class="party-meta">${esc(w.description)}</div></div><div class="party-meta">${Number(w.price)} coin</div>`;
      const buy = document.createElement('button');
      buy.type = 'button';
      buy.className = 'secondary';
      buy.textContent = 'Buy';
      buy.disabled = w.price > payload.coin;
      buy.addEventListener('click', async () => {
        try { await buyItem(opts.partyId, w.name); await refresh(); }
        catch (err) { showError((err as Error).message); }
      });
      row.appendChild(buy);
      box.appendChild(row);
    }
  };

  const renderSell = async (): Promise<void> => {
    if (opts.selfCharId === null) return;
    const { inventory } = await getInventory(opts.selfCharId);
    for (const item of inventory) {
      const row = document.createElement('div');
      row.className = 'party-row';
      row.innerHTML = `<div><div>${esc(item.name)}${item.equipped ? ' <span class="party-meta">(equipped)</span>' : ''}</div></div>`;
      const sell = document.createElement('button');
      sell.type = 'button';
      sell.className = 'secondary';
      sell.textContent = 'Sell';
      sell.disabled = item.equipped;
      sell.title = item.equipped ? 'unequip it first' : '';
      sell.addEventListener('click', async () => {
        try { await sellItem(opts.partyId, item.character_item_id); await refresh(); }
        catch (err) { showError((err as Error).message); }
      });
      row.appendChild(sell);
      box.appendChild(row);
    }
  };

  const showError = (msg: string): void => {
    const p = document.createElement('p');
    p.className = 'error';
    p.textContent = msg;
    box.appendChild(p);
  };

  btn.addEventListener('click', () => {
    open = !open;
    panel.hidden = !open;
    if (open) void refresh();
  });
  opts.ws.on('character.coin_changed', () => { void refresh(); });
  opts.ws.on('inventory.changed', () => { void refresh(); });
}

function esc(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]!));
}
